/**
 * Product usage drawer: per-channel quota gauges (reserved/confirmed/released),
 * a 7-day bars chart, and an invalidate-cache action. Ported from the console.
 */
import React from 'react';
import { View, Text, Pressable, ScrollView, StyleSheet, Platform } from 'react-native';
import Icon, { IconName } from './Icon';
import { Copyable } from './badges';
import { Btn } from './ui';
import { GaugeRing, Bars } from './charts';
import { useTheme } from '../../context/themeContext';
import { useToast } from '../toast';
import { invalidateCache } from '../../services/notification/usage';
import { handleApiError } from '../../utils/errorHandler';

export interface ChannelQuota { used: number; limit: number; reserved: number; confirmed: number; released: number; resets: string; licensed: boolean; }
export interface DrawerProduct {
  id: string; name: string; period: string;
  channels: { email: ChannelQuota; whatsapp: ChannelQuota; sms: ChannelQuota; push: ChannelQuota };
  history: Array<{ day: string; email: number; whatsapp: number }>;
}

const FALLBACK_HISTORY = [
  { day: 'Mon', email: 36, whatsapp: 24 }, { day: 'Tue', email: 48, whatsapp: 32 },
  { day: 'Wed', email: 62, whatsapp: 18 }, { day: 'Thu', email: 41, whatsapp: 22 },
  { day: 'Fri', email: 88, whatsapp: 30 }, { day: 'Sat', email: 54, whatsapp: 12 },
  { day: 'Sun', email: 33, whatsapp: 16 },
];

const CH: Array<{ key: keyof DrawerProduct['channels']; label: string; icon: IconName; color: string }> = [
  { key: 'email', label: 'Email', icon: 'mail', color: '#1E3A5F' },
  { key: 'whatsapp', label: 'WhatsApp', icon: 'message', color: '#25D366' },
  { key: 'sms', label: 'SMS', icon: 'phone', color: '#6A1B9A' },
  { key: 'push', label: 'Push', icon: 'bell', color: '#C2410C' },
];

export default function UsageDetailDrawer({ product, onClose }: { product: DrawerProduct; onClose: () => void }) {
  const { theme: { colors, font } } = useTheme();
  const toast = useToast();
  const [busy, setBusy] = React.useState(false);

  const invalidate = () => {
    setBusy(true);
    invalidateCache(product.id)
      .then(() => toast.info('Cache invalidated', { message: 'Next request will fetch fresh config' }))
      .catch(err => handleApiError(err, 'Could not invalidate'))
      .finally(() => setBusy(false));
  };

  const history = product.history.length ? product.history : FALLBACK_HISTORY;

  return (
    <View style={s.overlayWrap}>
      <Pressable style={s.scrim} onPress={onClose} />
      <View style={[s.drawer, { backgroundColor: colors.card, borderLeftColor: colors.line }]}>
        <View style={[s.head, { borderBottomColor: colors.line }]}>
          <View style={{ flex: 1 }}>
            <Text style={[s.eyebrow, { color: colors.ink3 }]}>Product Usage</Text>
            <Text style={{ color: colors.ink, fontSize: 20, fontWeight: '700', marginVertical: 4 }}>{product.name}</Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <Copyable value={product.id} />
              <Text style={{ color: colors.ink3, fontSize: 12 }}>{product.period}</Text>
            </View>
          </View>
          <Pressable onPress={onClose} hitSlop={8}><Icon name="x" size={18} color={colors.ink3} /></Pressable>
        </View>

        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 20, gap: 22 }}>
          {CH.filter(c => product.channels[c.key].licensed).map(c => {
            const d = product.channels[c.key];
            const pct = d.limit > 0 ? (d.used / d.limit) * 100 : 0;
            const exceeded = d.used >= d.limit;
            const warn = pct >= 80 && !exceeded;
            const stroke = exceeded ? '#C0392B' : warn ? '#F57C00' : c.color;
            return (
              <View key={c.key}>
                <View style={s.sectionTitle}>
                  <Icon name={c.icon} size={12} color={colors.ink3} />
                  <Text style={[s.sectionTitleText, { color: colors.ink3 }]}>{c.label} Quota</Text>
                  <View style={[s.rule, { backgroundColor: colors.line }]} />
                </View>
                <View style={{ flexDirection: 'row', gap: 20, alignItems: 'center', marginTop: 8 }}>
                  <GaugeRing pct={pct} used={d.used} limit={d.limit} color={stroke} />
                  <View style={{ flex: 1 }}>
                    <View style={{ flexDirection: 'row', gap: 18 }}>
                      {(['reserved', 'confirmed', 'released'] as const).map(k => (
                        <View key={k}>
                          <Text style={{ color: colors.ink4, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4 }}>{k}</Text>
                          <Text style={{ color: colors.ink, fontSize: 15, fontWeight: '600', fontFamily: font.mono }}>{d[k]}</Text>
                        </View>
                      ))}
                    </View>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 8 }}>
                      <Icon name="clock" size={11} color={colors.ink4} />
                      <Text style={{ color: colors.ink4, fontSize: 12 }}>Resets {d.resets} · 00:00 UTC</Text>
                    </View>
                  </View>
                </View>
              </View>
            );
          })}

          <View>
            <View style={s.sectionTitle}>
              <Icon name="activity" size={12} color={colors.ink3} />
              <Text style={[s.sectionTitleText, { color: colors.ink3 }]}>Last 7 days</Text>
              <View style={[s.rule, { backgroundColor: colors.line }]} />
            </View>
            <View style={{ marginTop: 10 }}><Bars history={history} /></View>
          </View>
        </ScrollView>

        <View style={[s.foot, { borderTopColor: colors.line }]}>
          <Btn label={busy ? 'Invalidating…' : 'Invalidate cache'} icon="refresh" size="sm" onPress={invalidate} disabled={busy} />
          <View style={{ flex: 1 }} />
          <Btn label="Close" kind="primary" onPress={onClose} />
        </View>
      </View>
    </View>
  );
}

const s = StyleSheet.create({
  overlayWrap: { ...StyleSheet.absoluteFill, flexDirection: 'row', ...(Platform.OS === 'web' ? { position: 'fixed' as any, zIndex: 200 } : {}) },
  scrim: { flex: 1, backgroundColor: 'rgba(15,23,42,0.35)' },
  drawer: { width: 440, maxWidth: '92%', borderLeftWidth: 1, height: '100%' },
  head: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, padding: 20, borderBottomWidth: 1 },
  eyebrow: { fontSize: 11, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.6 },
  sectionTitle: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  sectionTitleText: { fontSize: 11, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.5 },
  rule: { flex: 1, height: 1, marginLeft: 4 },
  foot: { flexDirection: 'row', alignItems: 'center', padding: 16, borderTopWidth: 1 },
});
